'use client';

import useSWR, { useSWRConfig } from 'swr';
import { SearchModes } from '@/domainLogic/index.ts';
import { fetchWithTracingAndLogging } from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';

import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';

import { BASE_PATH } from '@/app/components/search/utils/path';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { areTaxesIncludedInPrice } from '@/app/common/config/configuration/configurationUtilsClient.ts';
import { getMobileRates } from '@/app/components/search/components/Card/utils/getPromotionsUtils';
import { isValidFlightClass, getDestinationParameters, buildAccommodationInterval } from './common/common.helper';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import getSearchParams from '../search/common/getSearchParams';

export function computePricingSwrKey(params: {
  candidateKey: string;
  isPastCheckin: boolean;
  isHotelStaticPage: boolean;
  isApexCalendarReady: boolean;
  isRefreshingDates: boolean;
  hasCachedPricing: boolean;
}): string | undefined {
  const { candidateKey, isPastCheckin, isHotelStaticPage, isApexCalendarReady, isRefreshingDates, hasCachedPricing } =
    params;
  if (isPastCheckin || isHotelStaticPage || !isApexCalendarReady) return undefined;
  if (isRefreshingDates && !hasCachedPricing) return undefined;
  return candidateKey;
}

export function useCreateAsyncPricing() {
  const initialParams = useInitialParams();
  const {
    internalHotelId,
    pageType,
    transportId,
    isHotelStaticPage,
    rateApex,
    ratePriceContext,
    ratePriceContextSignature,
    ratePriceRates,
    isApexCalendarReady,
  } = initialParams;
  const {
    searchInfoData,
    searchInfoDetailData: { destination },
    localization,
    commonData,
  } = usePageWrapperData();

  const { searchMode } = searchInfoData;
  const { country, configValuesApp, isMeta } = localization;
  const { destinationType, id: destinationId } = destination;
  const { isApp, isMobileDevice, perimetersActive } = commonData;
  const { checkin, checkout, travellerGroups, accommodationOnly, isPastCheckin, isRefreshingDates } =
    useUrlSearchParams();
  const searchParams = getSearchParams();
  const departureAirport = searchParams[URLParameters.ORIGIN];
  const koProviderId = searchParams[URLParameters.KOPROVIDER_ID];
  const vcSearchId = searchParams[URLParameters.VC_SEARCH_ID];
  const feedContext = searchParams[URLParameters.FEED_CONTEXT];
  const airline = searchParams[URLParameters.AIRLINE];
  const direct = searchParams[URLParameters.DIRECT];
  const flightClass = searchParams[URLParameters.FLIGHT_CLASS] ?? undefined;
  const enforceProvider = searchParams[URLParameters.PROVIDER];
  const internalIdTransport = searchParams[URLParameters.INTERNAL_ID_TRANSPORT];
  const accommodation = buildAccommodationInterval(
    searchParams[URLParameters.CUSTOM_CHECKIN_CHECKOUT],
    searchMode === SearchModes.DP
  );
  const taxesIncluded = areTaxesIncludedInPrice({ searchMode, configValuesApp, country });

  const mobileRates = getMobileRates({
    configValuesApp,
    isMobileDevice,
    isMeta,
    isApp,
    isHotelOnly: accommodationOnly,
  });

  const searchParameters: ShoppingAsyncSearchParameters = {
    businessProfileId: localization.businessProfile,
    ...(perimetersActive?.length ? { perimeters: perimetersActive } : {}),
    ...(departureAirport && searchMode !== 'HO' ? { departure: { airports: [departureAirport] } } : {}),
    destination: {
      ...getDestinationParameters(destinationType, destinationId),
      hotelIds: [Number(internalHotelId)],
    },
    interval: {
      start: {
        date: checkin.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'),
      },
      end: {
        date: checkout.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'),
      },
    },
    ...(accommodation ? { accommodation } : {}),
    travellerGroups,
    currency: localization.currency,
    ...(koProviderId || enforceProvider
      ? {
          providers: {
            ...(koProviderId ? { excluded: [Number(koProviderId)] } : undefined),
            ...(enforceProvider ? { enforced: Number(enforceProvider) } : undefined),
          },
        }
      : undefined),
    mobile: mobileRates,
    ...(transportId || internalIdTransport
      ? {
          transport: {
            ...(transportId ? { id: transportId } : {}),
            ...(internalIdTransport ? { internalIdTransport } : {}),
          },
        }
      : isValidFlightClass(flightClass) || direct || airline
        ? {
            transport: {
              preferred: {
                ...(isValidFlightClass(flightClass) ? { class: flightClass } : {}),
                ...(direct ? (direct === '1' ? { directOnly: true } : { directOnly: false }) : {}),
                ...(airline ? { airline } : {}),
              },
            },
          }
        : {}),
  };

  const pricingIdRequest: PricingIdRequest = {
    searchParameters,
    additionalData: {
      excludedChargesIncluded: taxesIncluded,
      source: rateApex ? 'APEX' : 'TSX',
      ...(feedContext ? { feedContext } : {}),
    },
    ...(internalHotelId ? { hotelId: internalHotelId.toString() } : {}),
  };

  const candidateKey = JSON.stringify(pricingIdRequest);
  const { cache } = useSWRConfig();
  // Cache hit short-circuits the refresh gate: returning cached data fires no request, so the
  // SLIDERS-2194 "no double pricing-id call" invariant still holds.
  const hasCachedPricing = cache.get(candidateKey)?.data !== undefined;

  const key = computePricingSwrKey({
    candidateKey,
    isPastCheckin,
    isHotelStaticPage,
    isApexCalendarReady,
    isRefreshingDates,
    hasCachedPricing,
  });

  return useSWR(
    key,
    async () => {
      // MPH-785: a fetch for a key that already holds data is a refresh of the SAME pricing request —
      // a mutate() from the keep-alive tick, the visibility/review-entry renewals or the cart-creation
      // recovery — i.e. something the user never asked for. A key CHANGE instead means the user changed
      // the request (dates, transport, hotel, price context). Consumers use this to decide whether they
      // may act on the new pricing unattended (see PriceAndContinueButton). Read from the shared SWR
      // cache on purpose: a ref would live per subscriber, and any of them can own the running fetcher.
      const refreshedInPlace = key ? cache.get(key)?.data !== undefined : false;
      // Whether this request carried the apex rate context, which pins it to a target price. The
      // context is dropped right after this call, so consumers can only learn it from the response
      // (OffersProvider retries a failing rooms call as plain pricing when it is set). (MPH-1140)
      const withPriceContext = Boolean(ratePriceContext || ratePriceRates);

      return fetchWithTracingAndLogging<{ pricingId: string; time: number }>(
        `${BASE_PATH}/api/hotel-detail/pricing-id`,
        {
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            ...pricingIdRequest,
            // Kept out of pricingIdRequest so it never enters the SWR key.
            ...(isApp ? { isApp: true } : {}),
            ...(ratePriceContext ? { rctx: ratePriceContext } : {}),
            ...(ratePriceContextSignature ? { rsign: ratePriceContextSignature } : {}),
            ...(ratePriceRates ? { rates: ratePriceRates } : {}),
            // vcSearchId is not used in the swr key because after the search products is loaded it will change the vcSearchId
            // and will cause a new pricingId request, showing rooms skeletons and loading fetching rooms rates again.
            // Only send searchId when it survives the round-trip as an exact JS number: the MemSearch search_id can
            // exceed Number.MAX_SAFE_INTEGER and is already lossy by the time it reaches the URL, so a non-safe value
            // would be sent corrupted and rejected by pricing with SEARCH_INVALID_ID. Omitting it lets pricing compute
            // fresh (the same fallback the rooms-error retry relies on).
            ...(vcSearchId && Number.isSafeInteger(Number(vcSearchId)) ? { searchId: Number(vcSearchId) } : {}),
          }),
          method: 'POST',
        },
        getLoggingInfo(pageType, 'travel-api-async', 'init'),
        searchInfoData.searchTraceParent,
        commonData
      ).then((response) => {
        // After using the ratePriceContext once, we need to remove it to not use it in the next pricings (Price calendar, dates change, ...)
        initialParams.resetPriceContext?.();
        return { ...response, time: Date.now(), refreshedInPlace, withPriceContext };
      });
    },
    {
      // IMPORTANT: Do not set revalidateIfStale to false. This will cause the SWR to not revalidate the data when mounting the
      // component after the deduping interval
      // We want to revalidate the data when the deduping interval is over and the component is mounted. The pricing expires after
      // 30 minutes, if we call the cart creation with the expired pricing it will cause errors of product not found.
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 1000 * 60 * 29,
      refreshInterval: 1000 * 60 * 29,
    }
  );
}
